import { useResizeDetector } from 'react-resize-detector';
import { VariableSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { CSSProperties, useCallback, useEffect, useMemo, useRef } from 'react';

import { BottomButton } from '@@/LogViewer/LogList/BottomButton';
import { ProcessedLogsInterface } from '@@/LogViewer/types';
import { LogRow } from '@@/LogViewer/LogList/LogRow/LogRow';
import { useFocusKeyword } from '@@/LogViewer/hooks/useFocusKeyword';
import { useKeepAtBottom } from '@@/LogViewer/hooks/useKeepAtBottom';

import { useLogViewerContext } from '../context';
import { useSizeCache } from '../hooks/useSizeCache';

interface Props {
  logs: ProcessedLogsInterface;
}

export function LogList({ logs }: Props) {
  const { controllerState } = useLogViewerContext();
  const listRef = useRef<VariableSizeList>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const { getSize, resetCache } = useSizeCache();

  const listReset = useCallback(() => {
    // reset cache clears the cache list of log-row heights
    resetCache();
    listRef.current?.resetAfterIndex(0);
  }, [resetCache]);

  useFocusKeyword(listRef);

  const { isScrollAtBottom, onItemsRendered, onScroll, scrollToBottom } =
    useKeepAtBottom(outerRef, listRef);

  useResizeDetector({
    targetRef: outerRef,
    onResize: listReset,
    skipOnMount: true,
    refreshMode: 'debounce',
    refreshRate: 100,
  });
  // Reset the size cache when the logs change
  const logsFingerprint = useMemo(
    () =>
      `${logs.logs[0]?.line || ''}-${
        logs.logs[logs.logs.length - 1]?.line || ''
      }-${logs.logs.length}`,
    [logs.logs]
  );
  useEffect(() => {
    listReset();
  }, [logsFingerprint, controllerState, listReset]);

  return (
    <>
      <AutoSizer defaultHeight={750} disableWidth className="h-full">
        {({ height }) => (
          <VariableSizeList
            height={height}
            itemCount={logs.logs.length}
            itemSize={(index: number) =>
              getSize(
                index,
                logs,
                outerRef,
                controllerState.wrapLine,
                controllerState.showLineNumbers
              )
            }
            width="100%"
            onScroll={onScroll}
            onItemsRendered={onItemsRendered}
            ref={listRef}
            outerRef={outerRef}
            className="log-list"
            overscanCount={10}
          >
            {renderItem}
          </VariableSizeList>
        )}
      </AutoSizer>
      <BottomButton visible={!isScrollAtBottom} onClick={scrollToBottom} />
    </>
  );
}

function renderItem({ index, style }: { index: number; style: CSSProperties }) {
  return (
    <div style={{ ...style, width: 'unset' }}>
      <LogRow lineIndex={index} />
    </div>
  );
}
